import React, { useState, useCallback, useMemo } from 'react';
import { TabView, TabPanel } from 'primereact/tabview';
import { Dropdown } from 'primereact/dropdown';
import GenericFileExportButton from '../../../components/common/GenericFileExportButton';
import NewButton from '../../../components/NewButton/NewButton';
import useDealerPartsReturnProcessData, { DOMESTIC_TYPE } from './hooks/useDealerPartsReturnProcessData';
import DealerPartsReturnProcessTable from './components/DealerPartsReturnProcessTable';
import ReturnRequestDialog from './components/ReturnRequestDialog';
import { useLazyExportReturnProcessToExcelQuery } from '../../../api/endpoints/dealerPartsReturnProcess';
import { useLanguage } from '../../../contexts/LanguageContext';

const DealerPartsReturnProcess = () => {
    const { th } = useLanguage();
    const tabLabels = [
        { label: th('dealerPartsReturnProcess.tabs.draft'), icon: 'pi pi-file-edit', color: 'orange', status: 10, countKey: 'draft' },
        { label: th('dealerPartsReturnProcess.tabs.sentForApproval'), icon: 'pi pi-clock', color: 'blue', status: 20, countKey: 'sentForApproval' },
        { label: th('dealerPartsReturnProcess.tabs.approved'), icon: 'pi pi-check-circle', color: 'green', status: 30, countKey: 'approved' },
        { label: th('dealerPartsReturnProcess.tabs.rejected'), icon: 'pi pi-times-circle', color: 'red', status: 40, countKey: 'rejected' }
    ];

    const {
        apiData,
        isDomestic,
        isLoading,
        lazyParams,
        onPage,
        activeIndex,
        setActiveIndex,
        handleFilterChange,
        exportParams,
        tabCounts,
        activeFilters,
        filterOptions,
        sortField,
        sortOrder,
        onSort,
        domesticType,
        handleDomesticTypeChange,
        handleSendToRM,
        isSendingToRM,
        handleCancelRequest,
        isCancellingRequest,
        handleApproveRequest,
        isApproving,
        handleRejectRequest,
        isRejecting,
        handleReturnRequestSubmit,
        isSubmitting
    } = useDealerPartsReturnProcessData();

    const [returnRequestDialogVisible, setReturnRequestDialogVisible] = useState(false);
    const [selectedPartForReturn, setSelectedPartForReturn] = useState(null);

    const handleCreateReturnRequest = useCallback(() => {
        setSelectedPartForReturn({});
        setReturnRequestDialogVisible(true);
    }, []);

    const hideReturnRequestDialog = useCallback(() => {
        setReturnRequestDialogVisible(false);
        setTimeout(() => {
            setSelectedPartForReturn(null);
        }, 100);
    }, []);

    const onReturnRequestSuccess = useCallback(() => {
        hideReturnRequestDialog();
    }, [hideReturnRequestDialog]);

    const domesticTypeOptions = useMemo(
        () => [
            { label: th('common.all'), value: null },
            { label: th('dealerPartsReturnProcess.domestic'), value: DOMESTIC_TYPE.DOMESTIC },
            { label: th('dealerPartsReturnProcess.foreign'), value: DOMESTIC_TYPE.FOREIGN }
        ],
        [th]
    );

    return (
        <div className="card">
            <div className="flex justify-content-between align-items-center mb-4">
                <h1 className="m-0">{th('dealerPartsReturnProcess.title')}</h1>
                <div className="flex gap-2">
                    <GenericFileExportButton
                        useExportQuery={useLazyExportReturnProcessToExcelQuery}
                        exportParams={exportParams}
                        buttonLabel={th('dealerPartsReturnProcess.exportToExcel')}
                        buttonClassName="p-button-success"
                        icon="pi pi-file-excel"
                        fileNameExtractor={(result) => result?.fileName || 'bayi_parca_iade_sureci.xlsx'}
                        dataExtractor={(result) => result?.fileBase64}
                        contentTypeExtractor={(result) => result?.contentType}
                    />
                    <NewButton label={th('dealerPartsReturnProcess.actions.createReturnRequest')} onClick={handleCreateReturnRequest} />
                </div>
            </div>

            <div className="mb-3">
                <label htmlFor="domesticTypeFilter" className="block mb-2 font-medium">
                    {th('dealerPartsReturnProcess.domesticType')}
                </label>
                <Dropdown inputId="domesticTypeFilter" value={domesticType} options={domesticTypeOptions} onChange={(e) => handleDomesticTypeChange(e.value)} placeholder={th('common.all')} className="w-full md:w-20rem" />
            </div>

            <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)} className="dealer-parts-return-process-tabs">
                {tabLabels.map((tab, index) => {
                    const count = tabCounts?.[tab.countKey] ?? 0;
                    return (
                        <TabPanel
                            key={index}
                            header={
                                <span className="tab-header">
                                    <i className={`${tab.icon} mr-2`}></i>
                                    {tab.label}
                                    {count > 0 && <span className={`ml-2 px-2 py-1 border-round text-xs font-semibold bg-${tab.color}-100 text-${tab.color}-800`}>{count}</span>}
                                </span>
                            }
                            className="tab-content"
                        >
                            <DealerPartsReturnProcessTable
                                data={apiData?.rows}
                                totalCount={apiData?.totalCount}
                                lazyParams={lazyParams}
                                onPage={onPage}
                                loading={isLoading}
                                activeIndex={activeIndex}
                                domesticType={domesticType}
                                activeFilters={activeFilters}
                                onFiltersChange={handleFilterChange}
                                filterOptions={filterOptions}
                                sortField={sortField}
                                sortOrder={sortOrder}
                                onSort={onSort}
                                handleSendToRM={handleSendToRM}
                                isSendingToRM={isSendingToRM}
                                handleCancelRequest={handleCancelRequest}
                                isCancellingRequest={isCancellingRequest}
                                handleApproveRequest={handleApproveRequest}
                                isApproving={isApproving}
                                handleRejectRequest={handleRejectRequest}
                                isRejecting={isRejecting}
                            />
                        </TabPanel>
                    );
                })}
            </TabView>

            <ReturnRequestDialog
                visible={returnRequestDialogVisible}
                onHide={hideReturnRequestDialog}
                onSuccess={onReturnRequestSuccess}
                selectedPart={selectedPartForReturn}
                handleReturnRequestSubmit={handleReturnRequestSubmit}
                isSubmitting={isSubmitting}
                isDomestic={isDomestic}
            />
        </div>
    );
};

export default DealerPartsReturnProcess;
